import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { Dialog, GroupConfig, ReorderableList, Small } from "@fronterahealth/frontera-ui-components";

import {
  ReportGoalType,
  ReportTargetType,
  useBulkReassignReportGoalsMutation,
  useGetReportDomainsQuery,
  useReorderReportGoalsMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { GoalBadge } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalBadge";

interface GoalReorderItem {
  id: string;
  groupId: string;
  goalName: string;
  targetCount: number;
  status: string | null | undefined;
}

interface ReorderGoalsModalProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  goalList: ReportGoalType[];
}

export const ReorderGoalsModal: React.FC<ReorderGoalsModalProps> = ({ open, onClose, reportId, goalList }) => {
  const { data: reportDomainsData } = useGetReportDomainsQuery({ reportId });
  const reportDomains = reportDomainsData?.getReportDomains ?? [];
  const queryClient = useQueryClient();

  const initialItems = useMemo<GoalReorderItem[]>(
    () =>
      goalList.map((goal) => ({
        id: goal.id,
        groupId: goal.reportDomain?.id ?? "__uncategorized__",
        goalName: goal.goalName || "Untitled Goal",
        targetCount: ((unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || []).length,
        status: goal.status,
      })),
    [goalList],
  );

  const [items, setItems] = useState<GoalReorderItem[]>(initialItems);
  // Snapshot of groupIds at open time — used to detect domain changes on Apply
  const originalGroupIds = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (open) {
      setItems(initialItems);
      originalGroupIds.current = new Map(initialItems.map((item) => [item.id, item.groupId]));
    }
  }, [open]);

  // Separate memo so groups only recalculates when uncategorized presence changes, not on every drag
  const hasUncategorized = useMemo(() => items.some((item) => item.groupId === "__uncategorized__"), [items]);

  const groups: GroupConfig[] = useMemo(() => {
    const result: GroupConfig[] = [];

    if (hasUncategorized) {
      result.push({
        id: "__uncategorized__",
        renderHeader: () => (
          <Small displayType="loud" colorType="secondary" className="py-1">
            0. Uncategorized
          </Small>
        ),
      });
    }

    for (const [i, domain] of reportDomains.entries()) {
      result.push({
        id: domain.id,
        renderHeader: () => (
          <Small displayType="loud" colorType="secondary" className="py-1">
            {i + 1}. {domain.name}
          </Small>
        ),
      });
    }

    return result;
  }, [reportDomains, hasUncategorized]);

  const bulkReassignMutation = useBulkReassignReportGoalsMutation({
    onError: () => {
      notifyError("Failed to reassign goals to new domain");
    },
  });

  const reorderMutation = useReorderReportGoalsMutation({
    onError: () => {
      notifyError("Failed to reorder goals");
    },
  });

  const isPending = bulkReassignMutation.isPending || reorderMutation.isPending;

  const handleApply = async () => {
    const domainChanges = items
      .filter((item) => {
        const original = originalGroupIds.current.get(item.id);
        return original !== item.groupId && item.groupId !== "__uncategorized__";
      })
      .map((item) => ({ reportGoalId: item.id, newReportDomainId: item.groupId }));

    const reorderPayload = items.map((item, index) => ({
      reportGoalId: item.id,
      rankOrder: index,
    }));

    await Promise.all([
      domainChanges.length > 0 ? bulkReassignMutation.mutateAsync({ goals: domainChanges }) : Promise.resolve(),
      reorderMutation.mutateAsync({ goals: reorderPayload }),
    ]);

    queryClient.invalidateQueries({
      queryKey: ["get-reassessment-goals", reportId],
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      setOpen={() => {}}
      title="Reorder Goals"
      size="2xl"
      showDividers
      showCloseButton={false}
      primaryButton={{
        text: isPending ? "Applying..." : "Apply",
        onClick: handleApply,
        disabled: isPending,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
        disabled: isPending,
      }}
    >
      <div className="relative bg-limestone-50 p-4 -mx-6 max-h-[70vh] overflow-auto">
        {isPending && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/70">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-limestone-400" />
            <Small colorType="secondary">Applying changes…</Small>
          </div>
        )}
        <ReorderableList
          groups={groups}
          items={items}
          onReorder={setItems}
          enableMenu={false}
          renderItem={(item) => (
            <div className="flex w-full items-center justify-between gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-limestone-900 ph-no-capture">
                {item.goalName}
              </span>
              <div className="flex shrink-0 items-center gap-4">
                <GoalBadge status={item.status} />
                <Small colorType="secondary" className="whitespace-nowrap">
                  {item.targetCount} {item.targetCount === 1 ? "target" : "targets"}
                </Small>
              </div>
            </div>
          )}
        />
      </div>
    </Dialog>
  );
};
